import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
  getExampleNumber,
  isSupportedCountry,
  parsePhoneNumberFromString,
  validatePhoneNumberLength,
} from "libphonenumber-js/max";
import examples from "libphonenumber-js/mobile/examples";

// Phone validation by calling code, using libphonenumber's full ("max")
// metadata: real number patterns per country, not just digit counts.
//
// Same rules and messages as the mobile app
// (gulf-suprabhaatham-app/app/utils/phoneValidation.ts) — keep the two in
// step. The backend checks only the format ("+" and 8–15 digits in all).

export type PhoneCheck = {
  valid: boolean;
  // Message to show; null when there is nothing to report on the number
  // itself (e.g. the calling code is missing and reported on its own field).
  error: string | null;
};

let callingCodes: Set<string> | null = null;
const knownCallingCodes = (): Set<string> => {
  if (!callingCodes) {
    callingCodes = new Set(getCountries().map((c) => String(getCountryCallingCode(c))));
  }
  return callingCodes;
};

// The backend's rule for a stored phone: "+" then 8–15 digits in all.
const MIN_PHONE_DIGITS = 8;
const MAX_PHONE_DIGITS = 15;

// A calling code set on an E-Paper Settings country that libphonenumber has
// no data for (a custom code): only the backend's format rule can be checked.
const checkCustomCodePhone = (codeDigits: string, number: string, where: string): PhoneCheck => {
  if (number.startsWith("0")) {
    return { valid: false, error: "Enter the number without the leading 0" };
  }
  const total = codeDigits.length + number.length;
  if (total < MIN_PHONE_DIGITS) {
    return { valid: false, error: `Phone number is incomplete for ${where}` };
  }
  if (total > MAX_PHONE_DIGITS) {
    return { valid: false, error: `Phone number is too long for ${where}` };
  }
  return { valid: true, error: null };
};

// Region used for the example number: the selected country when it really
// uses this calling code, otherwise the first region that does.
const exampleRegion = (codeDigits: string, iso?: string): CountryCode | undefined => {
  if (iso && isSupportedCountry(iso) && getCountryCallingCode(iso) === codeDigits) {
    return iso;
  }
  return getCountries().find((c) => getCountryCallingCode(c) === codeDigits);
};

// Contact numbers only: toll-free, premium-rate, shared-cost and similar
// service numbers are rejected.
const CONTACT_TYPES = new Set(["MOBILE", "FIXED_LINE", "FIXED_LINE_OR_MOBILE"]);

const parseValid = (full: string, codeDigits: string) => {
  const parsed = parsePhoneNumberFromString(full);
  return parsed && parsed.countryCallingCode === codeDigits && parsed.isValid()
    ? parsed
    : undefined;
};

/**
 * Validates a local number (digits only, without the calling code) for a
 * calling code such as "+91".
 * @param where label used in messages, e.g. "India (+91)" or "+880".
 * @param iso   ISO alpha-2 code of the selected country, when there is one.
 * @param configured the code belongs to an active E-Paper Settings country;
 *        if libphonenumber doesn't know it, the backend's format rule applies.
 */
export const checkPhone = (
  dialCode: string | undefined,
  local: string | undefined,
  where: string,
  iso?: string,
  configured = false
): PhoneCheck => {
  const number = String(local ?? "").trim();
  if (!number) return { valid: false, error: "Please enter a phone number" };
  if (!/^\d+$/.test(number)) {
    return { valid: false, error: "Phone number can contain digits only" };
  }
  const codeDigits = String(dialCode ?? "").replace(/\D/g, "");
  if (!codeDigits) return { valid: false, error: null };
  if (!knownCallingCodes().has(codeDigits)) {
    if (configured) return checkCustomCodePhone(codeDigits, number, where);
    return { valid: false, error: `+${codeDigits} is not a valid country calling code` };
  }

  const full = `+${codeDigits}${number}`;
  const parsed = parseValid(full, codeDigits);
  // libphonenumber drops a trunk 0 typed after the code ("+971 0501234567"),
  // but the number is saved as code + digits, so it must not be there.
  // (Where the 0 is part of the number, e.g. Italy, it is kept and matches.)
  if (number.startsWith("0") && (!parsed || parsed.nationalNumber !== number)) {
    const withoutZero = number.replace(/^0+/, "");
    if (parsed || parseValid(`+${codeDigits}${withoutZero}`, codeDigits)) {
      return { valid: false, error: "Enter the number without the leading 0" };
    }
  }
  if (parsed) {
    const type = parsed.getType();
    if (type && !CONTACT_TYPES.has(type)) {
      return { valid: false, error: `Enter a mobile or landline number for ${where}` };
    }
    return { valid: true, error: null };
  }

  const region = exampleRegion(codeDigits, iso);
  const example = region ? getExampleNumber(region, examples)?.nationalNumber : undefined;
  const hint = example ? ` (e.g. ${example})` : "";
  let length = validatePhoneNumberLength(full);
  // The library only reports lengths that fit no number type at all; compare
  // with a typical number so "50123456" for UAE still reads as incomplete.
  if (!length && example && number.length !== example.length) {
    length = number.length < example.length ? "TOO_SHORT" : "TOO_LONG";
  }
  switch (length) {
    case "TOO_SHORT":
      return { valid: false, error: `Phone number is incomplete for ${where}${hint}` };
    case "TOO_LONG":
      return { valid: false, error: `Phone number is too long for ${where}${hint}` };
    case "INVALID_LENGTH":
      return { valid: false, error: `Phone number has the wrong number of digits for ${where}${hint}` };
    default:
      return { valid: false, error: `Enter a valid phone number for ${where}${hint}` };
  }
};
